import React, { useState, useEffect, useContext } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton,
    CircularProgress,
    Button,
} from "@mui/material";
import { CloseSquare, Eye, Trash } from "iconsax-react";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls";
import { LocaleContext } from "../../../routing/LangRouter";
import { t } from "i18next";

const MedicalRequestHistory = ({ handleNewRequest }) => {
    const accessToken = localStorage.getItem("accessToken");

    const [requests, setRequests] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);

    const handleViewResult = id => {
        // Navigate to the medical-tourism-result page with the id as a parameter
        // navigate(`/${locale}/medical-tourism-result`);
        navigate({
            pathname: `/${locale}/medical-tourism-result`,
            search: `?${createSearchParams({ id })}`,
        });
    };

    useEffect(() => {
        if (accessToken) {
            setRequests([]);
            fetchRequests(accessToken); // Fetch new request history when the token changes
        }
    }, [accessToken]); // This will run every time the accessToken changes

    const fetchRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${BASE_URL}/api/v2/itinerary/health/tour/list/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                setRequests(response.data.data);
            } else {
                throw new Error("Unexpected data structure received from the server");
            }
        } catch (error) {
            console.error("Error fetching requests:", error);
            setError(t("noRequestYet"));
        } finally {
            setLoading(false);
        }
    };

    const handleClickOpen = request => {
        setSelectedRequest(request);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedRequest(null);
    };

    const handleDelete = async (id, event) => {
        // event.stopPropagation();
        // event.preventDefault();
        try {
            await axios.delete(`${BASE_URL}/api/v2/itinerary/health/tour/delete/?pk=${id}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setRequests(requests.filter(request => request.id !== id));
            handleClose();
        } catch (error) {
            console.error("Error deleting request:", error);
        }
    };
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleDeleteClick = () => {
        setShowConfirmation(true);
    };

    const handleConfirmDelete = () => {
        handleDelete(selectedRequest?.id);
        setShowConfirmation(false);
    };

    const handleCancelDelete = () => {
        setShowConfirmation(false);
    };

    const getStatusColor = status => {
        switch (status) {
            case 1:
                return "#ffeb3b";
            case 2:
                return "#2196f3";
            case 3:
                return "#4caf50";
            default:
                return "#000";
        }
    };

    const getStatusText = status => {
        switch (status) {
            case 1:
                return t("underReview");
            case 2:
                return t("inProgress");
            case 3:
                return t("finished");
            default:
                return t("unspecified");
        }
    };

    const getGenderText = gender => {
        switch (gender) {
            case 1:
                return t("man");
            case 2:
                return t("woman");
            case 3:
                return t("other");
            default:
                return t("unspecified");
        }
    };

    const getServicesList = services => {
        return services.map(service => service.service).join(", ");
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: "center", color: "error.main", mt: 2 }}>
                <Typography>{error}</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2, padding: "0 20px" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t("requestHistory")}
                </Typography>
            </Box>
            {requests.length === 0 ? (
                <Typography sx={{ textAlign: "center", mt: 2, padding: "0 20px" }}>{t("noRequestYet")}</Typography>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, padding: "0 20px" }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableCell sx={{ fontWeight: "bold", color: "#555", padding: "16px" }}>{t("medicalServices")}</TableCell>
                                <TableCell sx={{ fontWeight: "bold", color: "#555", padding: "16px" }}>{t("date")}</TableCell>
                                <TableCell sx={{ fontWeight: "bold", color: "#555", padding: "16px" }}>{t("status")}</TableCell>
                                <TableCell sx={{ fontWeight: "bold", color: "#555", padding: "16px" }}>{t("view")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.map(request => (
                                <TableRow
                                    key={request.id}
                                    sx={{
                                        "&:hover": { backgroundColor: "#f5f5f5", cursor: "pointer" },
                                        transition: "background-color 0.3s ease-in-out",
                                    }}
                                    onClick={() => handleClickOpen(request)}
                                >
                                    <TableCell sx={{ padding: "16px" }}>{getServicesList(request.services)}</TableCell>
                                    <TableCell sx={{ padding: "16px" }}>{new Date(request.request_date).toLocaleDateString("fa-IR")}</TableCell>
                                    <TableCell sx={{ padding: "16px" }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                backgroundColor: getStatusColor(request.status),
                                                color: "#000",
                                                padding: "4px 8px",
                                                borderRadius: "4px",
                                                fontSize: "10px",
                                            }}
                                        >
                                            {getStatusText(request.status)}
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ padding: "16px" }}>
                                        <IconButton color="success" aria-label="show">
                                            <Eye />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold", paddingRight: 6, marginTop: 4, padding: "16px" }}>{t("requestDetails")}</DialogTitle>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        left: 8,
                        top: 8,
                        color: theme => theme.palette.grey[500],
                    }}
                >
                    <CloseSquare color="#000000" />
                </IconButton>
                <DialogContent sx={{ padding: "24px" }}>
                    {selectedRequest && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("name")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {`${selectedRequest.first_name} ${selectedRequest.last_name}`}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("age")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.age}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("gender")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {getGenderText(selectedRequest.gender)}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("departureCountry")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.origin_country.name}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("requestDate")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {new Date(selectedRequest.request_date).toLocaleDateString("fa-IR")}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("medicalServices")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {getServicesList(selectedRequest.services)}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("tripDuration")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.estimated_trip_time}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("destinationCity")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.destination_city.name}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("phone")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.mobile_phone}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("email")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.email || t("notAvailable")}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("status")}
                                </Typography>
                                <Box
                                    component="span"
                                    sx={{
                                        backgroundColor: getStatusColor(selectedRequest.status),
                                        color: "#000",
                                        padding: "6px 12px",
                                        borderRadius: "4px",
                                    }}
                                >
                                    {getStatusText(selectedRequest.status)}
                                </Box>
                            </Box>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {t("illnessDescription")}
                                </Typography>
                                <Typography variant="body1" sx={{ color: "#333" }}>
                                    {selectedRequest.illness_description}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                {/* <DialogActions sx={{ justifyContent: "flex-start", padding: "16px" }}>
                    {selectedRequest?.status === 1 ? (
                        <Button variant="contained" color="error" onClick={() => handleDelete(selectedRequest?.id)} startIcon={<Trash />} fullWidth>
                            {t("deleteRequest")}
                        </Button>
                    ) : selectedRequest?.status === 2 ? (
                        <Button variant="contained" color="warning" disabled fullWidth>
                            {t("inProgrss")}
                        </Button>
                    ) : selectedRequest?.status === 3 ? (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleViewResult(selectedRequest?.id)}
                            startIcon={<Eye />}
                            fullWidth
                        >
                            {t("view")}
                        </Button>
                    ) : null}
                </DialogActions> */}
                <DialogActions sx={{ justifyContent: "flex-start", padding: "16px" }}>
                    {selectedRequest?.status === 1 ? (
                        showConfirmation ? (
                            <>
                                <Button variant="contained" color="error" onClick={handleConfirmDelete} fullWidth sx={{ mr: 1 }}>
                                    {t("yes")}
                                </Button>
                                <Button variant="contained" color="primary" onClick={handleCancelDelete} fullWidth>
                                    {t("no")}
                                </Button>
                            </>
                        ) : (
                            <Button variant="contained" color="error" onClick={handleDeleteClick} startIcon={<Trash />} fullWidth>
                                {t("deleteRequest")}
                            </Button>
                        )
                    ) : selectedRequest?.status === 2 ? (
                        <Button variant="contained" color="warning" disabled fullWidth>
                            {t("inProgrss")}
                        </Button>
                    ) : selectedRequest?.status === 3 ? (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleViewResult(selectedRequest?.id)}
                            startIcon={<Eye />}
                            fullWidth
                        >
                            {t("view")}
                        </Button>
                    ) : null}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MedicalRequestHistory;
